import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';
import ApiClient from '../../util/api-client';
import type {
  Customer,
  CustomerDeliveryAddress,
  Invoice,
  InvoiceCreateInput,
  InvoiceVoidInput,
  InvoiceItem,
  TaxCode,
} from '@repo/api-contract';
import dayjs from 'dayjs';

export enum InvoiceModalMode {
  CREATE = 'create',
  VIEW = 'view',
  EDIT = 'edit',
}

export type CustomerDeliveryAddressWithId = CustomerDeliveryAddress & {
  id: string;
  type: 'customer' | 'delivery' | 'billing';
};

export interface InvoiceModalState {
  stateIsChanged: boolean;
  isOpenInvoiceModal: boolean;
  invoiceIdToEdit?: string;
  deleteInvoiceModal: boolean;
  invoiceToDelete: Invoice | undefined;
  mode: InvoiceModalMode;
  loading: boolean;
  updateCallback?: (newInvoice: Invoice | undefined) => Promise<void>;
  createCallback?: (newInvoice: Invoice | undefined) => Promise<void>;
  deleteCallback?: (newInvoice: Invoice | undefined) => Promise<void>;
  formData: InvoiceCreateInput;
  addresses: CustomerDeliveryAddressWithId[];
  customer: Customer | undefined;
  customerFreight: string;
  orderFieldCode: string;
  orderFieldColor: 'primary' | 'success' | 'error';
}

const initialFormData: InvoiceCreateInput = {
  code: '',
  orderId: '',
  estimatedShipDate: 0,
  customerDeposit: 0,
  depositReference: '',
  shipMethod: '',
  deadline: 0,
  invoiceDate: 0,
  paymentTerms: [],
  poShipTo: {},
  invoiceShipTo: {},
  customer: {
    code: '',
    companyName: '',
    CustomerId: '',
  },
  items: [],
  customerFreight: 0,
  applyServiceCharge: false,
  monthlyServiceCharge: 0,
  gracePeriod: 0,
  commissionAdjustmentExplanation: '',
  commissionAdjustment: 0,
  invoiceMemos: '',
  taxCode: {
    TeamId: '',
    rate: 0,
    TaxCodeId: '',
    description: '',
    default: false,
    codeName: '',
    createdAt: 0,
    updatedAt: 0,
    createdBy: '',
  },
  commissionable: false,
};

// Initialize state
const initialState: InvoiceModalState = {
  stateIsChanged: false,
  isOpenInvoiceModal: false,
  invoiceIdToEdit: undefined,
  deleteInvoiceModal: false,
  invoiceToDelete: undefined,
  mode: InvoiceModalMode.CREATE,
  loading: false,
  formData: initialFormData,
  addresses: [],
  customer: undefined,
  customerFreight: '',
  orderFieldCode: '',
  orderFieldColor: 'primary',
};

export const invoiceModalSlice = createSlice({
  name: 'invoiceModal',
  initialState,
  reducers: {
    openInvoiceModal: (
      state,
      action: PayloadAction<
        | {
            createCallback?: (newInvoice: Invoice | undefined) => Promise<void>;
            resetState?: boolean;
          }
        | undefined
      >,
    ) => {
      // If resetState is true, reset the formData and any other relevant fields
      if (action.payload?.resetState) {
        return {
          ...initialState,
          isOpenInvoiceModal: true,
          createCallback: action.payload.createCallback,
          formData: {
            ...initialFormData,
            invoiceDate: dayjs().unix(),
          }, // Reset form data back to initial
        };
      }

      // Otherwise, open the modal without resetting
      return {
        ...state,
        isOpenInvoiceModal: true,
        createCallback: action.payload?.createCallback,
      };
    },
    openEditInvoiceModal: (
      state,
      action: PayloadAction<{
        invoiceIdToEdit: string;
        mode?: InvoiceModalMode;
        updateCallback?: (user: Invoice | undefined) => Promise<void>;
      }>,
    ) => {
      return {
        ...state,
        invoiceIdToEdit: action.payload.invoiceIdToEdit,
        isOpenInvoiceModal: true,
        updateCallback: action.payload?.updateCallback,
        isEditingInvoice: true,
        mode: action.payload?.mode || InvoiceModalMode.EDIT,
      };
    },
    setFormData: (state, action: PayloadAction<InvoiceCreateInput>) => {
      // Update form data in redux whenever form changes
      return {
        ...state,
        formData: action.payload,
        stateIsChanged: true,
      };
    },
    setAddresses: (
      state,
      action: PayloadAction<CustomerDeliveryAddressWithId[]>,
    ) => {
      return {
        ...state,
        addresses: action.payload,
      };
    },
    setCustomer: (state, action: PayloadAction<Customer | undefined>) => {
      return {
        ...state,
        customer: action.payload,
      };
    },
    setCustomerFreight: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        customerFreight: action.payload,
      };
    },
    setOrderField: (
      state,
      action: PayloadAction<{
        orderFieldCode?: string;
        orderFieldColor?: 'primary' | 'success' | 'error';
      }>,
    ) => {
      return {
        ...state,
        orderFieldCode: action.payload.orderFieldCode ?? state.orderFieldCode,
        orderFieldColor:
          action.payload.orderFieldColor ?? state.orderFieldColor,
      };
    },
    addItemsToInvoice: (state, action: PayloadAction<InvoiceItem[]>) => {
      return {
        ...state,
        formData: {
          ...state.formData,
          items: [...state.formData.items, ...action.payload],
        },
        stateIsChanged: true,
      };
    },
    setMode: (state, action: PayloadAction<InvoiceModalMode>) => ({
      ...state,
      mode: action.payload,
    }),
    closeModal: (
      state,
      action: PayloadAction<{ resetState?: boolean } | undefined>,
    ) => {
      if (action.payload?.resetState) return { ...initialState };
      return {
        ...state,
        isOpenInvoiceModal: false,
        deleteInvoiceModal: false,
      };
    },
    openDeleteInvoiceModal: (
      state,
      action: PayloadAction<{
        invoiceToDelete: Invoice;
        deleteCallback?: (user: Invoice | undefined) => Promise<void>;
      }>,
    ) => {
      return {
        ...state,
        deleteInvoiceModal: true,
        invoiceToDelete: action.payload.invoiceToDelete,
        deleteCallback: action.payload.deleteCallback,
      };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
  },
});

export const {
  openInvoiceModal,
  openEditInvoiceModal,
  addItemsToInvoice,
  closeModal,
  openDeleteInvoiceModal,
  setMode,
  setLoading,
  setAddresses,
  setCustomer,
  setCustomerFreight,
  setOrderField,
  setFormData,
} = invoiceModalSlice.actions;

// Thunks
export const fetchInvoiceToEdit =
  (id: string) =>
  async (dispatch: Dispatch): Promise<Invoice> => {
    try {
      dispatch(setLoading(true));
      const invoice: Invoice = await ApiClient.invoiceGetById.query(id);
      return invoice;
    } finally {
      dispatch(setLoading(false));
    }
  };

export const deleteInvoice =
  (deleteInput: InvoiceVoidInput) =>
  async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(setLoading(true));
      await ApiClient.voidInvoice.mutate(deleteInput);
    } finally {
      dispatch(setLoading(false));
    }
  };

export default invoiceModalSlice.reducer;
